import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { BatchManager } from '../base/batchUtils';
import { hasUserRole, MODERATOR_PRIVILEGE_ROLES } from '../../utilities/orgAccess';

/**
 * Clean up test data for an organisation
 * Removes members, groups, and codes while preserving admin/moderator roles
 */
export async function cleanupTestData(orgId: string): Promise<void> {
  try {
    // 1. Get all members including temporary users
    const [regularUsersSnapshot, tempUsersSnapshot] = await Promise.all([
      // Regular users
      getDocs(query(
        collection(db, 'Users'),
        where(`Organisations.${orgId}`, '!=', null)
      )),
      // Temporary users
      getDocs(query(
        collection(db, 'Users'),
        where('isTemporary', '==', true),
        where('organisationId', '==', orgId)
      ))
    ]);

    const batchManager = new BatchManager();

    // Handle regular users - delete members/students, preserve admin/moderator/teacher roles
    for (const docSnapshot of regularUsersSnapshot.docs) {
      const userData = docSnapshot.data();
      const orgMembership = userData.Organisations[orgId];
      const userRole = orgMembership?.role;
      const userRoles = orgMembership?.roles as string[] | undefined;
      
      // Skip admins, superadmins, moderators, and facilitators
      if (
        hasUserRole(userRole, [...MODERATOR_PRIVILEGE_ROLES, 'facilitator']) ||
        (Array.isArray(userRoles) && (
          userRoles.some((r) => hasUserRole(r, [...MODERATOR_PRIVILEGE_ROLES, 'facilitator']))
        ))
      ) {
        continue;
      }

      // For regular members/students, delete their entire document
      await batchManager.addOperation(batch => {
        batch.delete(docSnapshot.ref);
      });
    }

    // Delete temporary users completely
    for (const docSnapshot of tempUsersSnapshot.docs) {
      await batchManager.addOperation(batch => {
        batch.delete(docSnapshot.ref);
      });
    }

    // Clear all groups' memberIds
    const groupsSnapshot = await getDocs(query(
      collection(db, 'groups'),
      where('organisationId', '==', orgId)
    ));
    
    for (const docSnapshot of groupsSnapshot.docs) {
      await batchManager.addOperation(batch => {
        batch.update(docSnapshot.ref, { 
          memberIds: [],
          updatedAt: serverTimestamp()
        });
      });
    }

    // Delete all generated codes
    const codesSnapshot = await getDocs(
      collection(db, 'Organisations', orgId, 'generatedCodes')
    );
    
    for (const docSnapshot of codesSnapshot.docs) {
      await batchManager.addOperation(batch => {
        batch.delete(docSnapshot.ref);
      });
    }

    // Delete all access codes for this organisation
    const accessCodesSnapshot = await getDocs(query(
      collection(db, 'accessCodes'),
      where('organisationId', '==', orgId)
    ));
    
    for (const docSnapshot of accessCodesSnapshot.docs) {
      await batchManager.addOperation(batch => {
        batch.delete(docSnapshot.ref);
      });
    }

    // Commit final batch if there are any pending operations
    await batchManager.commit();

  } catch (error) {
    console.error('Error cleaning up test data:', error);
    throw error;
  }
}
